<script setup>
import { goToLogin } from '@/utils/loginFlow'
import { ref, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { useRouter } from 'vue-router';
import AdminPagination from '@/components/AdminPagination.vue';

const router = useRouter();

// 检查是否登录
const checkLogin = () => {
  if (!localStorage.getItem('jwt_token')) {
    goToLogin(router)
    return false
  }
  return true
}

// State
const rechargeList = ref([]);
const total = ref(0);
const currentPage = ref(1);
const pageSize = ref(10);
const loading = ref(false);
const bankSlips = ref([]);
const bankTotal = ref(0);
const bankPage = ref(1);
const bankPageSize = ref(10);
const bankLoading = ref(false);

// Format amount helper
function formatAmount(value) {
  if (!value && value !== 0) return '0.00';
  return (Number(value) / 100).toFixed(2);
}

// Format date helper
function formatDate(timestamp) {
  return new Date(timestamp * 1000).toLocaleString();
}

function formatDay(timestamp) {
  if (!timestamp) return '—'
  const d = new Date(Number(timestamp) * 1000)
  if (Number.isNaN(d.getTime())) return '—'
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

// Get recharge list
async function getRechargeList() {
  try {
    loading.value = true;
    const token = localStorage.getItem('jwt_token');
    const response = await fetch('/api/v1/recharge/list', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        page: currentPage.value,
        pageSize: pageSize.value,
        payMethod: 0,
      })
    });

    const data = await response.json();
    if (data.code === 200) {
      rechargeList.value = data.data.orders || [];
      total.value = data.data.total || 0;
    } else {
      ElMessage.error(data.message || '获取充值记录失败');
    }
  } catch (error) {
    console.error('Failed to fetch recharge list:', error);
    ElMessage.error('获取充值记录失败');
  } finally {
    loading.value = false;
  }
}

// Handle page change
function handlePageChange(page) {
  currentPage.value = page;
  getRechargeList();
}

// Handle page size change
function handleSizeChange(size) {
  pageSize.value = size;
  currentPage.value = 1;
  getRechargeList();
}

async function getBankSlips() {
  try {
    bankLoading.value = true;
    const token = localStorage.getItem('jwt_token');
    const response = await fetch('/api/v1/recharge/bank/slip/list', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        page: bankPage.value,
        pageSize: bankPageSize.value
      })
    });
    const data = await response.json();
    if (data.code === 200) {
      bankSlips.value = data.data?.items || [];
      bankTotal.value = data.data?.total || 0;
    }
  } catch (error) {
    console.error('Failed to fetch bank slips:', error);
  } finally {
    bankLoading.value = false;
  }
}

function slipStatusLabel(s) {
  return ({ pending: '待审核', approved: '已入账', rejected: '已拒绝' })[s] || s;
}

onMounted(() => {
  // 检查是否登录
  if (!checkLogin()) {
    return
  }
  getRechargeList();
  getBankSlips();
});
</script>

<template>
  <div class="recharge-list-container">
    <div class="card">
      <h2>充值记录</h2>
      <el-table
        v-loading="loading"
        :data="rechargeList"
        style="width: 100%"
        border
      >
        <el-table-column
          prop="orderNo"
          label="订单号"
          width="180"
        />
        <el-table-column
          prop="amount"
          label="金额"
          width="120"
        >
          <template #default="{ row }">
            ¥{{ formatAmount(row.amount) }}
          </template>
        </el-table-column>
        <el-table-column
          prop="status"
          label="状态"
          width="100"
        >
          <template #default="{ row }">
            <el-tag :type="row.status === 'completed' ? 'success' : 'danger'">
              {{ row.status === 'completed' ? '成功' : '失败' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          prop="createdAt"
          label="创建时间"
          width="180"
        >
          <template #default="{ row }">
            {{ formatDate(row.createdAt) }}
          </template>
        </el-table-column>
      </el-table>

      <AdminPagination
        v-model:current-page="currentPage"
        v-model:page-size="pageSize"
        :total="total"
        @size-change="handleSizeChange"
        @current-change="handlePageChange"
      />
    </div>
    <div class="card" style="margin-top: 20px">
      <h2>银行转账单据</h2>
      <el-table v-loading="bankLoading" :data="bankSlips" border>
        <el-table-column prop="slipNo" label="单据号" min-width="170" />
        <el-table-column label="金额" width="110">
          <template #default="{ row }">¥{{ formatAmount(row.amountCents) }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template #default="{ row }">{{ slipStatusLabel(row.status) }}</template>
        </el-table-column>
        <el-table-column prop="remark" label="附言" min-width="140" show-overflow-tooltip />
        <el-table-column label="转账日期" width="120">
          <template #default="{ row }">{{ formatDay(row.transferAt) }}</template>
        </el-table-column>
        <el-table-column label="提交时间" width="180">
          <template #default="{ row }">{{ formatDate(row.createdAt) }}</template>
        </el-table-column>
      </el-table>
      <AdminPagination
        v-model:current-page="bankPage"
        v-model:page-size="bankPageSize"
        :total="bankTotal"
        @size-change="(s) => { bankPageSize = s; bankPage = 1; getBankSlips() }"
        @current-change="(p) => { bankPage = p; getBankSlips() }"
      />
    </div>
  </div>
</template>

<style scoped>
.recharge-list-container {
  padding: 20px;
  max-width: none;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}

.card {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 20px;
}

.pagination-container {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}
</style> 